"use client";

import { createContext, useContext, useEffect, type ReactNode } from "react";
import { usePathname, useRouter } from "next/navigation";
import type { Session, User } from "@supabase/supabase-js";
import { createClient } from "@/lib/supabase/client";
import type { User as DbUser } from "@otalytics/shared";
import { useAuthAnalytics } from "./use-auth-analytics";
import { useAuthSession } from "./use-auth-session";
import { useDbUser } from "./use-db-user";
import { safeNext } from "./safe-next";

export type AuthContextType = {
  user: User | null;
  dbUser: DbUser | null;
  session: Session | null;
  isLoading: boolean;
  signInWithEmail: (
    email: string,
    password: string,
    next?: string
  ) => Promise<void>;
  signUpWithEmail: (
    email: string,
    password: string,
    next?: string
  ) => Promise<void>;
  signOut: () => Promise<void>;
  refreshDbUser: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const { user, session, authEvent, isLoading: isSessionLoading } = useAuthSession();
  const {
    dbUser,
    isLoading: isDbUserLoading,
    error: dbUserError,
    refreshDbUser,
  } = useDbUser(session, authEvent);
  useAuthAnalytics(dbUser, authEvent);

  useEffect(() => {
    if (authEvent === "SIGNED_OUT") {
      if (pathname.startsWith("/reset-password")) {
        return;
      }
      router.push("/login");
    }
  }, [authEvent, pathname, router]);

  // `isDbUserLoading` alone is not enough. `useDbUser` initialises it to false
  // and only flips it inside an effect, so in the commit where `session` first
  // becomes non-null it is still false while `dbUser` is null — and React runs
  // child effects before parent ones, so consumers saw `isLoading: false` with
  // no `dbUser` and decided against a user they had not loaded yet. That gap
  // gated admins out of the destination report and bounced them off
  // /admin/queues. Auth stays "loading" until the question is actually
  // answered; `dbUserError` is the other answer, or a failed /api/auth/me
  // would leave every consumer waiting forever.
  const isLoading =
    isSessionLoading ||
    (session ? isDbUserLoading || (!dbUser && !dbUserError) : false);

  const signInWithEmail = async (
    email: string,
    password: string,
    next?: string
  ): Promise<void> => {
    const supabase = createClient();
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    router.push(safeNext(next) ?? "/dashboard");
  };

  const signUpWithEmail = async (
    email: string,
    password: string,
    next?: string
  ): Promise<void> => {
    const supabase = createClient();
    // State the confirmation destination explicitly instead of relying on GOTRUE_SITE_URL
    // plus the API send-email hook lining up. The hook rewrites signup redirects to
    // /login?confirmed=1 (apps/api/.../auth-hooks.controller.ts), which the login page
    // reads to show the "email confirmed" notice — so this is the same destination, just
    // asserted by the client. Not /auth/callback: that route only exchanges a PKCE `?code=`
    // and the confirmation link goes through GoTrue's token-hash /verify endpoint, which
    // never issues one.
    //
    // `next` rides along so the confirmation click lands on the city the visitor
    // was buying rather than the dashboard. Validated on the way out as well as
    // on the way back in: never round-trip an unchecked redirect through email.
    const safe = safeNext(next);
    const confirmUrl = `${window.location.origin}/login?confirmed=1${
      safe ? `&next=${encodeURIComponent(safe)}` : ""
    }`;

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: confirmUrl,
      },
    });
    if (error) throw error;
  };

  const signOut = async (): Promise<void> => {
    const supabase = createClient();
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const value = {
    user,
    dbUser,
    session,
    isLoading,
    signInWithEmail,
    signUpWithEmail,
    signOut,
    refreshDbUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return { ...context, isAuthenticated: !!context.user };
}
